// components/CommentThread.tsx
import React, { useState, useRef, useEffect } from 'react';
import { Close } from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
import type { Comment } from './CommentTypes';
import CommentItem from './CommentItem';
import CustomTooltip from '../../CustomToolTip/CustomToolTip';
import './Comment.css';

interface CommentThreadProps {
  comments: Comment[];
  onAddComment: (text: string, replyTo?: string) => void;
  onEditComment: (commentId: string, text: string) => void;
  onDeleteComment: (commentId: string) => void;
  onResolveThread: () => void;
  onClose: () => void;
  position: { top: number; left: number };
  isLoading?: boolean;
  isResolving?: boolean;
}

const CommentThread: React.FC<CommentThreadProps> = ({
  comments,
  onAddComment,
  onEditComment,
  onDeleteComment,
  onResolveThread,
  onClose,
  position,
  isLoading = false,
  isResolving = false,
}) => {
  const [newComment, setNewComment] = useState('');
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const threadRef = useRef<HTMLDivElement>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newComment.trim() && !isLoading) {
      onAddComment(newComment, replyTo || undefined);
      setNewComment('');
      setReplyTo(null);
    }
  };

  const handleReply = (commentId: string) => {
    if (!isLoading) {
      setReplyTo(commentId);
    }
  };

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (threadRef.current && !threadRef.current.contains(event.target as Node)) {
        const target = event.target as HTMLElement;
        if (target?.closest('.ProseMirror') || target?.classList?.contains('comment-highlight')) {
          return;
        }
        onClose();
      }
    };

    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  return (
    <div
      ref={threadRef}
      className="comment-thread"
      onMouseDown={(e) => e.stopPropagation()}
      style={{
        position: 'fixed',
        top: position.top,
        left: position.left,
        width: 280,
        maxHeight: 400,
        backgroundColor: 'white',
        border: '1px solid #dee2e6',
        borderRadius: 8,
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        zIndex: 1000,
        padding: 12,
        overflowY: 'auto',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 12,
        }}
      >
        <h4
          style={{
            margin: 0,
            fontSize: 14,
            fontWeight: 700,
            color: '#000000',
            fontFamily: 'Inter, sans-serif',
          }}
        >
          Comments
        </h4>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {comments.length > 0 && (
            <CustomTooltip title={'Mark this thread as resolve'} variant="bottom" offset={[0, 0]}>
              <button
                onClick={onResolveThread}
                disabled={isResolving}
                className="resolve-btn"
                style={{
                  fontSize: 11,
                  color: isResolving ? '#6c757d' : '#007bff',
                  background: 'none',
                  border: 'none',
                  cursor: isResolving ? 'not-allowed' : 'pointer',
                  padding: '2px 0',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                <img src="images/right.svg" />
              </button>
            </CustomTooltip>
          )}

          <CustomTooltip title={'Close '} variant="bottom" offset={[0, 0]}>
            <button
              onClick={onClose}
              disabled={isLoading || isResolving}
              style={{
                background: 'none',
                border: 'none',
                cursor: isLoading || isResolving ? 'not-allowed' : 'pointer',
                padding: 4,
                color: '#6c757d',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Close style={{ fontSize: 16 }} />
            </button>
          </CustomTooltip>
        </div>
      </div>

      <div style={{ marginBottom: 12, maxHeight: 100, overflowY: 'auto' }}>
        {comments.map((comment) => (
          <CommentItem
            key={comment.id}
            comment={comment}
            onReply={handleReply}
            onEdit={onEditComment}
            onDelete={onDeleteComment}
            isDisabled={isLoading}
            showReplyFormating={true}
          />
        ))}
      </div>

      {replyTo && (
        <div
          style={{
            padding: '6px 8px',
            backgroundColor: '#e3f2fd',
            borderRadius: 4,
            marginBottom: 8,
            fontSize: 12,
            color: '#0641e6',
            fontFamily: 'Inter, sans-serif',
          }}
        >
          Replying to comment
          <button
            onClick={() => {
              setReplyTo(null);
              setNewComment('');
            }}
            disabled={isLoading}
            style={{
              marginLeft: 8,
              background: 'none',
              border: 'none',
              color: '#0641e6',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: 11,
            }}
          >
            Cancel
          </button>
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        style={{ display: 'flex', gap: 8, flexDirection: 'column', alignItems: 'end' }}
      >
        <textarea
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          placeholder={replyTo ? 'Write a reply...' : 'Add a comment here'}
          disabled={isLoading}
          style={{
            width: '100%',
            minHeight: 60,
            padding: '6px 8px',
            border: '1px solid #007bff',
            borderRadius: 4,
            fontSize: 12,
            outline: 'none',
            backgroundColor: isLoading ? '#f8f9fa' : 'white',
            fontFamily: 'Inter, sans-serif',
            resize: 'vertical',
            boxSizing: 'border-box',
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape' && !isLoading) {
              setReplyTo(null);
              if (!newComment.trim()) {
                onClose();
              }
            }
          }}
        />
        <button
          type="submit"
          disabled={!newComment.trim() || isLoading}
          style={{
            padding: '8px 16px',
            backgroundColor: newComment.trim() && !isLoading ? '#007bff' : '#e9ecef',
            color: newComment.trim() && !isLoading ? 'white' : '#6c757d',
            border: 'none',
            borderRadius: 4,
            cursor: newComment.trim() && !isLoading ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'Inter, sans-serif',
            fontSize: 12,
            minWidth: '60px',
          }}
        >
          {isLoading ? <CircularProgress size={14} style={{ color: '#6c757d' }} /> : 'Add'}
        </button>
      </form>
    </div>
  );
};

export default CommentThread;
